import React, { useState, useEffect } from "react";
import { Building2, MapPin, Clock, Tag, Phone, FileText, LogOut, Check, HelpCircle, X, Plus, Trash2, ArrowUp, ArrowDown, MessageCircle, Settings2, BadgeCheck, RefreshCw, Tags, Zap, Smartphone, Link2, Send, Users, Copy, QrCode, AlertTriangle } from "lucide-react";

const API = "https://app.jedichatai.com";
const C = {
  bg: "#0B1120", card: "#151B2E", border: "#232B45",
  text: "#F2F4F8", sub: "#8E93A8", accent: "#007AFF", ok: "#30D158", bad: "#FF453A",
};

const ZONES = [
  "Africa/Monrovia", "Africa/Accra", "Africa/Lagos", "Africa/Abidjan",
  "Africa/Freetown", "Africa/Nairobi", "Africa/Johannesburg", "Africa/Cairo",
  "America/Toronto", "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Sao_Paulo", "Europe/London", "Europe/Dublin",
  "Europe/Paris", "Europe/Berlin", "Europe/Lisbon", "Asia/Dubai", "Asia/Karachi",
  "Asia/Kolkata", "Asia/Singapore", "Asia/Manila", "Australia/Sydney", "UTC",
];

const FIELDS: { key: string; label: string; hint: string; icon: any; lines?: number; select?: boolean }[] = [
  { key: "business_name", label: "Business name", hint: "What customers know you as", icon: Building2 },
  { key: "description", label: "What your business does", hint: "A sentence or two", icon: FileText, lines: 3 },
  { key: "location", label: "Location", hint: "Street, city", icon: MapPin, lines: 2 },
  { key: "hours", label: "Opening hours", hint: "Mon to Sat, 9am to 7pm. Closed Sunday.", icon: Clock, lines: 2 },
  { key: "timezone", label: "Time zone", hint: "So the AI knows when you are open", icon: Clock, select: true },
  { key: "services", label: "Services and prices", hint: "One per line. Haircut 500 LRD", icon: Tag, lines: 5 },
  { key: "contact_info", label: "Contact details", hint: "Phone, email, website", icon: Phone, lines: 2 },
  { key: "policies", label: "Policies", hint: "Refunds, bookings, deposits", icon: FileText, lines: 3 },
  { key: "extra_instructions", label: "Anything else the AI should know", hint: "Free text. Tone, things to avoid, common questions", icon: FileText, lines: 4 },
];

function api(path: string, method = "GET", body?: any) {
  const token = localStorage.getItem("jedichatai_token");
  return fetch(API + path, {
    method,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.detail || "Something went wrong");
    return d;
  });
}

function Login({ onIn }: { onIn: () => void }) {
  const [mode, setMode] = useState<"in" | "up" | "code">("in");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true); setErr("");
    try {
      if (mode === "up") {
        await api("/api/v1/auth/register", "POST",
          { email, password, full_name: name, account_type: "business" });
      }
      const d = await api("/api/v1/auth/login", "POST", { email, password });
      if (d.requires_verification) { setMode("code"); setErr(""); setBusy(false); return; }
      if (!d.access_token) { setErr(d.message || "Could not sign in"); setBusy(false); return; }
      localStorage.setItem("jedichatai_token", d.access_token);

      // Every sign-in route issues its own token, so the account type is
      // checked here in one place rather than in six.
      const me = await api("/api/v1/auth/me");
      if (me.account_type !== "business") {
        localStorage.removeItem("jedichatai_token");
        setErr("That is a personal account. Create a business account to use JediChatAI for Business.");
        setBusy(false); return;
      }
      onIn();
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };

  const verify = async () => {
    setBusy(true); setErr("");
    try {
      await api("/api/v1/auth/verify-email", "POST", { email, otp: code });
      const d = await api("/api/v1/auth/login", "POST", { email, password });
      if (!d.access_token) { setErr(d.message || "Could not sign in"); setBusy(false); return; }
      localStorage.setItem("jedichatai_token", d.access_token);
      const me = await api("/api/v1/auth/me");
      if (me.account_type !== "business") {
        localStorage.removeItem("jedichatai_token");
        setErr("That is a personal account. Create a business account to use JediChatAI for Business.");
        setBusy(false); return;
      }
      onIn();
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };

  if (mode === "code") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "grid", placeItems: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <img src="/logo.png" alt="" width={56} height={56}
          style={{ borderRadius: 12, display: "block", marginBottom: 14 }}/>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 6 }}>Check your email</div>
        <div style={{ color: C.sub, marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
          We sent a 6 digit code to {email}. It expires in 10 minutes.
        </div>
        <input placeholder="6 digit code" value={code} inputMode="numeric"
          onChange={e => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
          onKeyDown={e => e.key === "Enter" && verify()}
          style={{ ...inp, letterSpacing: 6, textAlign: "center", fontSize: 20, fontWeight: 700 }}/>
        {err && <div style={{ color: C.bad, fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>{err}</div>}
        <button onClick={verify} disabled={busy || code.length < 6}
          style={{ ...btn, opacity: busy || code.length < 6 ? .6 : 1 }}>
          {busy ? "Checking..." : "Verify and continue"}
        </button>
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: C.sub }}>
          <button onClick={() => { setMode("in"); setErr(""); setCode(""); }}
            style={{ background: "none", border: 0, color: C.accent, cursor: "pointer",
              fontSize: 13, fontWeight: 600, padding: 0 }}>
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "grid", placeItems: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <img src="/logo.png" alt="" width={56} height={56}
          style={{ borderRadius: 12, display: "block", marginBottom: 14 }}/>
        <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", marginBottom: 4 }}>
          JediChat<span style={{ color: C.accent }}>AI</span>
        </div>
        <div style={{ color: C.sub, marginBottom: 26, fontSize: 14 }}>for Business</div>

        {mode === "up" && (
          <input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} style={inp}/>
        )}
        <input placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} style={inp}/>
        <input placeholder="Password" type="password" value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && go()} style={inp}/>
        {err && <div style={{ color: C.bad, fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>{err}</div>}
        <button onClick={go} disabled={busy || !email || !password} style={{ ...btn, opacity: busy ? .6 : 1 }}>
          {busy ? "Please wait..." : mode === "up" ? "Create business account" : "Sign in"}
        </button>

        <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: C.sub }}>
          {mode === "in" ? "New to JediChatAI for Business? " : "Already have a business account? "}
          <button onClick={() => { setMode(mode === "in" ? "up" : "in"); setErr(""); }}
            style={{ background: "none", border: 0, color: C.accent, cursor: "pointer",
              fontSize: 13, fontWeight: 600, padding: 0 }}>
            {mode === "in" ? "Create one" : "Sign in"}
          </button>
        </div>
        <div style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: C.sub, lineHeight: 1.6 }}>
          A business account is separate from a personal one, so use a different
          email address from your personal JediChatAI account.
        </div>
      </div>
    </div>
  );
}

function Gaps() {
  const [gaps, setGaps] = useState<any[]>([]);
  const load = () => api("/api/v1/business/gaps").then(d => setGaps(d.gaps || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const dismiss = (topic: string) =>
    api("/api/v1/business/gaps/resolve", "POST", { topic }).then(load).catch(() => {});

  if (!gaps.length) return null;
  return (
    <div style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 14, padding: 16, marginBottom: 18 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
        <HelpCircle size={16} color="#FF9F0A"/>
        <span style={{ fontWeight: 700, fontSize: 14 }}>Customers asked about this</span>
      </div>
      <p style={{ color: C.sub, fontSize: 13, lineHeight: 1.6, margin: "0 0 12px" }}>
        Your profile does not cover these, so the AI said it would check. Add them below and it will answer next time.
      </p>
      {gaps.map(g => (
        <div key={g.topic} style={{ display: "flex", alignItems: "center", gap: 10,
          padding: "9px 11px", background: "#0F1626", borderRadius: 10, marginBottom: 7 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, textTransform: "capitalize" }}>{g.topic}</div>
            <div style={{ fontSize: 11, color: C.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              "{g.example}"
            </div>
          </div>
          <span style={{ fontSize: 12, color: "#FF9F0A", fontWeight: 700, whiteSpace: "nowrap" }}>
            {g.times}x
          </span>
          <button onClick={() => dismiss(g.topic)} title="Dismiss"
            style={{ background: "none", border: 0, color: C.sub, cursor: "pointer", padding: 2, display: "flex" }}>
            <X size={15}/>
          </button>
        </div>
      ))}
    </div>
  );
}

function Catalog() {
  const [items, setItems] = useState<any[]>([]);
  const [currency, setCurrency] = useState("USD");
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api("/api/v1/business/catalog")
      .then(d => { setItems(d.items || []); setCurrency(d.currency || "USD"); })
      .catch(() => {});
  }, []);

  const set = (i: number, k: string, val: any) =>
    setItems(items.map((it, n) => n === i ? { ...it, [k]: val } : it));
  const add = () => setItems([...items, { name: "", price: "", description: "", available: true }]);
  const remove = (i: number) => setItems(items.filter((_, n) => n !== i));
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const copy = items.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setItems(copy);
  };

  const save = async () => {
    setErr(""); setSaved(false);
    try {
      await api("/api/v1/business/catalog", "POST", { items, currency });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e: any) { setErr(e.message); }
  };

  return (
    <div style={{ marginBottom: 22 }}>
      <h2 style={{ margin: "8px 0 6px", fontSize: 20 }}>Services and prices</h2>
      <p style={{ color: C.sub, fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
        The AI quotes these exactly. Switch something off and it will say you are
        not offering it at the moment, rather than pretending it does not exist.
      </p>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: C.sub }}>Currency</span>
        <input value={currency} onChange={e => setCurrency(e.target.value.toUpperCase())}
          maxLength={10} style={{ ...inp, marginBottom: 0, width: 90, textAlign: "center" }}/>
      </div>

      {items.map((it, i) => (
        <div key={i} style={{ background: C.card, border: "1px solid " + C.border,
          borderRadius: 12, padding: 12, marginBottom: 9, opacity: it.available === false ? 0.55 : 1 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input value={it.name || ""} placeholder="Service or product"
              onChange={e => set(i, "name", e.target.value)}
              style={{ ...inp, marginBottom: 0, flex: 1 }}/>
            <input value={it.price ?? ""} placeholder="Price" inputMode="decimal"
              onChange={e => set(i, "price", e.target.value)}
              style={{ ...inp, marginBottom: 0, width: 110, textAlign: "right" }}/>
          </div>
          <input value={it.description || ""} placeholder="Optional note, e.g. includes a wash"
            onChange={e => set(i, "description", e.target.value)}
            style={{ ...inp, marginBottom: 8, fontSize: 13 }}/>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12,
              color: C.sub, cursor: "pointer", flex: 1 }}>
              <input type="checkbox" checked={it.available !== false}
                onChange={e => set(i, "available", e.target.checked)}/>
              Available
            </label>
            <button onClick={() => move(i, -1)} style={iconBtn} title="Move up"><ArrowUp size={14}/></button>
            <button onClick={() => move(i, 1)} style={iconBtn} title="Move down"><ArrowDown size={14}/></button>
            <button onClick={() => remove(i)} style={{ ...iconBtn, color: C.bad }} title="Remove"><Trash2 size={14}/></button>
          </div>
        </div>
      ))}

      <button onClick={add} style={{ ...iconBtn, width: "100%", padding: "11px",
        border: "1px dashed " + C.border, borderRadius: 10, justifyContent: "center",
        gap: 7, marginBottom: 10, fontSize: 13 }}>
        <Plus size={15}/> Add a service
      </button>

      {err && <div style={{ color: C.bad, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <button onClick={save} style={{ ...btn, background: saved ? C.ok : C.accent }}>
        {saved ? "Saved" : "Save services"}
      </button>
    </div>
  );
}

const LABEL_COLOURS = ["#30D158", "#FF9F0A", "#007AFF", "#FF453A", "#BF5AF2", "#8E93A8"];

function QrConnect() {
  const [status, setStatus] = useState<any>({ connected: false });
  const [qr, setQr] = useState("");
  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState(false);
  const [rejected, setRejected] = useState(false);

  const check = () => api("/api/v1/baileys/status").then(setStatus).catch(() => {});
  useEffect(() => { check(); }, []);

  // While the QR is showing, poll: the code rotates and the connection can
  // complete at any moment.
  useEffect(() => {
    if (!open || status.connected) return;
    const t = setInterval(async () => {
      try {
        const s2 = await api("/api/v1/baileys/status");
        setStatus(s2);
        if (s2.connected) {
          // A business line must be a WhatsApp Business account. "unknown"
          // is allowed through — a failed check must not lock out a real shop.
          if (s2.accountType === "personal") {
            await api("/api/v1/baileys/disconnect", "POST", {}).catch(() => {});
            setStatus({ connected: false });
            setRejected(true);
            setOpen(false); setQr("");
            return;
          }
          setOpen(false); setQr(""); return;
        }
        const d = await api("/api/v1/baileys/qr");
        if (d.qr) setQr(d.qr);
      } catch (e) {}
    }, 3000);
    return () => clearInterval(t);
  }, [open, status.connected]);

  const start = async () => {
    setStarting(true); setOpen(true);
    try {
      await api("/api/v1/baileys/start", "POST", {});
      const d = await api("/api/v1/baileys/qr");
      if (d.qr) setQr(d.qr);
    } catch (e) {}
    setStarting(false);
  };

  const disconnect = async () => {
    try { await api("/api/v1/baileys/disconnect", "POST", {}); setQr(""); setOpen(false); check(); }
    catch (e) {}
  };

  return (
    <div style={{ marginBottom: 22 }}>
      <h2 style={{ margin: "8px 0 6px", fontSize: 20 }}>Quick connect with a QR code</h2>
      <p style={{ color: C.sub, fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
        Scan with the phone that holds your business WhatsApp. No Meta account
        needed, so you can start straight away.
      </p>

      {rejected && (
        <div style={{ background: "#2A0F12", border: "1px solid #7A2530", borderRadius: 10,
          padding: 12, marginBottom: 12, fontSize: 13, color: "#F0A0A8", lineHeight: 1.55 }}>
          That number is on the ordinary WhatsApp app, so it was disconnected.
          A business line needs the free <strong>WhatsApp Business</strong> app.
          Install it, set up your business profile, then scan again.
        </div>
      )}
      <div style={{ display: "flex", gap: 9, background: "#2A1F0A", border: "1px solid #6B4E10",
        borderRadius: 10, padding: 11, marginBottom: 12 }}>
        <AlertTriangle size={16} color="#FF9F0A" style={{ flexShrink: 0, marginTop: 1 }}/>
        <div style={{ fontSize: 12.5, color: "#E8C77A", lineHeight: 1.55 }}>
          This uses an unofficial connection. It is quick, but WhatsApp can ban
          numbers that use it. For a number your business depends on, connect
          the official WhatsApp Business API above instead.
        </div>
      </div>

      {status.connected ? (
        <div style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            Connected{status.phone ? " · " + status.phone : ""}
          </div>
          <button onClick={disconnect} style={{ ...iconBtn, color: C.bad, fontSize: 13,
            border: "1px solid " + C.border, borderRadius: 9, padding: "7px 12px", marginTop: 8 }}>
            Disconnect
          </button>
        </div>
      ) : open ? (
        <div style={{ background: "#fff", borderRadius: 12, padding: 16, textAlign: "center" }}>
          {qr ? (
            <>
              <img src={qr} alt="QR code" style={{ width: 232, height: 232 }}/>
              <div style={{ color: "#333", fontSize: 12.5, marginTop: 8 }}>
                WhatsApp, Settings, Linked devices, Link a device
              </div>
            </>
          ) : (
            <div style={{ color: "#555", fontSize: 13, padding: 40 }}>
              {starting ? "Preparing your code..." : "Waiting for a code..."}
            </div>
          )}
        </div>
      ) : (
        <button onClick={start} style={{ ...btn }}>
          <span style={{ display: "inline-flex", gap: 7, alignItems: "center" }}>
            <QrCode size={15}/> Show QR code
          </span>
        </button>
      )}
    </div>
  );
}

function Team() {
  const [data, setData] = useState<any>({ team: [], is_owner: true });
  const [email, setEmail] = useState("");
  const [invite, setInvite] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => api("/api/v1/business/team").then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    setMsg("");
    try {
      const d = await api("/api/v1/business/team", "POST", { email: email.trim() });
      setInvite(d.invite_code); setEmail(""); load();
    } catch (e: any) { setMsg(e.message); }
  };
  const remove = async (id: number) => {
    try { await api("/api/v1/business/team", "POST", { remove_id: id }); load(); } catch (e) {}
  };
  const join = async () => {
    setMsg("");
    try {
      const d = await api("/api/v1/business/team/accept", "POST", { invite_code: joinCode.trim() });
      setMsg("You have joined " + (d.business_name || "the team") + ". Reload to see their inbox.");
      setJoinCode("");
    } catch (e: any) { setMsg(e.message); }
  };

  const link = invite ? window.location.origin + "/?join=" + invite : "";

  return (
    <div style={{ marginBottom: 22 }}>
      <h2 style={{ margin: "8px 0 6px", fontSize: 20 }}>Your team</h2>
      <p style={{ color: C.sub, fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
        Invite staff to answer customers from the same WhatsApp number. They sign
        in with their own account and see this inbox.
      </p>

      {data.team?.map((m: any) => (
        <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10,
          background: C.card, border: "1px solid " + C.border, borderRadius: 11,
          padding: "10px 12px", marginBottom: 7 }}>
          <Users size={15} color={C.sub}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{m.full_name || m.email}</div>
            <div style={{ fontSize: 11.5, color: C.sub }}>
              {m.accepted_at ? m.email : "Invitation not yet accepted"}
            </div>
          </div>
          {data.is_owner && (
            <button onClick={() => remove(m.id)} style={{ ...iconBtn, color: C.bad }}>
              <X size={14}/>
            </button>
          )}
        </div>
      ))}

      {data.is_owner ? (
        <>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <input value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && add()}
              placeholder="Staff email address"
              style={{ ...inp, marginBottom: 0, flex: 1 }}/>
            <button onClick={add} disabled={!email.trim()} style={{ ...iconBtn, padding: 8 }}>
              <Plus size={17}/>
            </button>
          </div>
          {invite && (
            <div style={{ marginTop: 11, background: "#0F1626", borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, color: C.sub, marginBottom: 7 }}>
                Send them this link. It works once.
              </div>
              <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                <input readOnly value={link} style={{ ...inp, marginBottom: 0, flex: 1, fontSize: 12 }}/>
                <button onClick={() => navigator.clipboard.writeText(link)}
                  style={{ ...iconBtn, padding: 8 }}><Copy size={15}/></button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ color: C.sub, fontSize: 13 }}>Only the account owner can manage the team.</div>
      )}

      <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid " + C.border }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 7 }}>Have an invitation?</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={joinCode} onChange={e => setJoinCode(e.target.value)}
            placeholder="Paste your invitation code"
            style={{ ...inp, marginBottom: 0, flex: 1 }}/>
          <button onClick={join} disabled={!joinCode.trim()} style={{ ...iconBtn, padding: 8 }}>
            <Link2 size={16}/>
          </button>
        </div>
      </div>

      {msg && <div style={{ color: C.sub, fontSize: 13, marginTop: 10 }}>{msg}</div>}
    </div>
  );
}

function Verification() {
  const [st, setSt] = useState<any>(null);
  const [f, setF] = useState<any>({ legal_name: "", registration_no: "", registration_country: "", public_url: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const d = await api("/api/v1/business/verification");
      setSt(d);
      if (d && d.fields) setF({
        legal_name: d.fields.legal_name || "",
        registration_no: d.fields.registration_no || "",
        registration_country: d.fields.registration_country || "",
        public_url: d.fields.public_url || "",
      });
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api("/api/v1/business/verification", "POST", f);
      await load();
    } catch (e: any) { setErr(e.message || "Could not submit"); }
    setBusy(false);
  };

  if (!st) return null;
  const status = st.status || "unverified";
  const when = (x: string) => x ? new Date(x).toLocaleDateString() : "";
  const card: any = { background: C.card, border: "1px solid " + C.border, borderRadius: 14, padding: 16, marginBottom: 12 };

  if (status === "verified") return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <BadgeCheck size={22} color={C.accent}/>
        <span style={{ fontSize: 16, fontWeight: 800 }}>{st.fields.legal_name || "Verified business"}</span>
      </div>
      <p style={{ color: C.sub, fontSize: 13, margin: "8px 0 0" }}>
        Verified on {when(st.reviewed_at)}.
      </p>
    </div>
  );

  if (status === "pending") return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Clock size={16} color={C.sub}/>
        <span style={{ fontSize: 14, fontWeight: 700 }}>Verification in review</span>
      </div>
      <p style={{ color: C.sub, fontSize: 13, margin: 0 }}>
        Sent {when(st.submitted_at)}. These are checked by hand, so give it a day or two.
      </p>
    </div>
  );

  const F: any[] = [
    ["legal_name", "Registered business name", "Exactly as it appears on your registration"],
    ["registration_country", "Country of registration", "Liberia, Ghana, Canada"],
    ["registration_no", "Registration or licence number", "Or leave empty and give a link below"],
    ["public_url", "A public link we can check", "Your website or Facebook page"],
  ];

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <BadgeCheck size={18} color={C.accent}/>
        <span style={{ fontSize: 14, fontWeight: 700 }}>Get verified</span>
      </div>
      <p style={{ color: C.sub, fontSize: 13, marginTop: 0, lineHeight: 1.6 }}>
        A blue tick beside your business name, so customers know the shop is real.
        Checked by hand. No documents needed.
      </p>
      {status === "rejected" && st.note ? (
        <div style={{ background: "rgba(255,69,58,0.12)", border: "1px solid " + C.bad, borderRadius: 10, padding: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.bad, marginBottom: 4 }}>Not approved</div>
          <div style={{ fontSize: 13 }}>{st.note}</div>
        </div>
      ) : null}
      {F.map((row: any) => (
        <div key={row[0]} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{row[1]}</div>
          <input value={f[row[0]]} placeholder={row[2]}
            onChange={e => setF({ ...f, [row[0]]: e.target.value })}
            style={{ ...inp, marginBottom: 0 }}/>
        </div>
      ))}
      {err ? <div style={{ color: C.bad, fontSize: 13, marginBottom: 8 }}>{err}</div> : null}
      <button onClick={submit} disabled={busy}
        style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 10,
                 padding: "10px 16px", fontWeight: 700, fontSize: 14, cursor: "pointer", opacity: busy ? 0.6 : 1 }}>
        {busy ? "Sending..." : "Send for review"}
      </button>
    </div>
  );
}

function WabaConnect() {
  const [state, setState] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api("/api/v1/business/waba").then(setState).catch(() => {});
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true); setErr("");
    try { await api("/api/v1/business/waba", "POST", form); setForm({}); load(); }
    catch (e: any) { setErr(e.message); }
    setBusy(false);
  };
  const disconnect = async () => {
    try { await api("/api/v1/business/waba", "DELETE"); load(); } catch (e) {}
  };

  return (
    <div style={{ marginBottom: 22 }}>
      <h2 style={{ margin: "8px 0 6px", fontSize: 20 }}>WhatsApp Business number</h2>
      <p style={{ color: C.sub, fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
        Connect your own WhatsApp Business API number so replies come from you.
        You will find both values in Meta Business Suite under WhatsApp, API setup.
      </p>

      {state?.connected ? (
        <div style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 12, padding: 14 }}>
          <div style={{ display: "flex", gap: 9, alignItems: "center", marginBottom: 8 }}>
            <Smartphone size={16} color={C.ok}/>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              {state.waba?.display_number || "Connected"}
            </span>
          </div>
          <div style={{ fontSize: 12, color: C.sub, marginBottom: 11 }}>
            Number ID {state.waba?.phone_number_id}
          </div>
          <button onClick={disconnect} style={{ ...iconBtn, color: C.bad, fontSize: 13,
            border: "1px solid " + C.border, borderRadius: 9, padding: "7px 12px" }}>
            Disconnect
          </button>
        </div>
      ) : (
        <div style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 12, padding: 14 }}>
          <input placeholder="Phone number ID" value={form.phone_number_id || ""}
            onChange={e => setForm({ ...form, phone_number_id: e.target.value.trim() })} style={inp}/>
          <input placeholder="Display number, e.g. +231 77 000 0000" value={form.display_number || ""}
            onChange={e => setForm({ ...form, display_number: e.target.value })} style={inp}/>
          <input placeholder="Permanent access token" type="password" value={form.access_token || ""}
            onChange={e => setForm({ ...form, access_token: e.target.value.trim() })} style={inp}/>
          {err && <div style={{ color: C.bad, fontSize: 13, marginBottom: 10 }}>{err}</div>}
          <button onClick={connect} disabled={busy || !form.phone_number_id || !form.access_token}
            style={{ ...btn, opacity: busy ? .6 : 1 }}>
            <span style={{ display: "inline-flex", gap: 7, alignItems: "center" }}>
              <Link2 size={15}/> {busy ? "Connecting..." : "Connect number"}
            </span>
          </button>
          <div style={{ fontSize: 11.5, color: C.sub, marginTop: 10, lineHeight: 1.6 }}>
            Your token is encrypted before it is stored and is never shown again.
          </div>
        </div>
      )}
    </div>
  );
}

function QuickReplies() {
  const [items, setItems] = useState<any[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api("/api/v1/business/quick-replies").then(d => setItems(d.replies || [])).catch(() => {});
  }, []);

  const set = (i: number, k: string, val: string) =>
    setItems(items.map((it, n) => n === i ? { ...it, [k]: val } : it));
  const add = () => setItems([...items, { shortcut: "", body: "" }]);
  const remove = (i: number) => setItems(items.filter((_, n) => n !== i));

  const save = async () => {
    try {
      await api("/api/v1/business/quick-replies", "POST", { replies: items });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) {}
  };

  return (
    <div style={{ marginBottom: 22 }}>
      <h2 style={{ margin: "8px 0 6px", fontSize: 20 }}>Quick replies</h2>
      <p style={{ color: C.sub, fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
        Answers you send often. Give each one a shortcut so you can drop it into
        a chat without retyping.
      </p>

      {items.map((it, i) => (
        <div key={i} style={{ background: C.card, border: "1px solid " + C.border,
          borderRadius: 12, padding: 12, marginBottom: 9 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <Zap size={14} color={C.accent}/>
            <span style={{ color: C.sub, fontSize: 14 }}>/</span>
            <input value={it.shortcut || ""} placeholder="hours"
              onChange={e => set(i, "shortcut", e.target.value.replace(/[^a-zA-Z0-9-]/g, "").toLowerCase())}
              style={{ ...inp, marginBottom: 0, flex: 1 }}/>
            <button onClick={() => remove(i)} style={{ ...iconBtn, color: C.bad }}><Trash2 size={14}/></button>
          </div>
          <textarea value={it.body || ""} rows={2}
            placeholder="We are open Monday to Saturday, 9am to 7pm."
            onChange={e => set(i, "body", e.target.value)}
            style={{ ...inp, marginBottom: 0, resize: "vertical", fontFamily: "inherit" }}/>
        </div>
      ))}

      <button onClick={add} style={{ ...iconBtn, width: "100%", padding: 11,
        border: "1px dashed " + C.border, borderRadius: 10, justifyContent: "center",
        gap: 7, marginBottom: 10, fontSize: 13 }}>
        <Plus size={15}/> Add a quick reply
      </button>
      <button onClick={save} style={{ ...btn, background: saved ? C.ok : C.accent }}>
        {saved ? "Saved" : "Save quick replies"}
      </button>
    </div>
  );
}

function LabelManager() {
  const [labels, setLabels] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [colour, setColour] = useState(LABEL_COLOURS[0]);

  const load = () => api("/api/v1/business/labels")
    .then(d => setLabels(d.labels || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    try { await api("/api/v1/business/labels", "POST", { name: n, colour }); setName(""); load(); }
    catch (e) {}
  };
  const del = async (id: number) => {
    try { await api("/api/v1/business/labels", "POST", { delete_id: id }); load(); } catch (e) {}
  };

  return (
    <div style={{ marginBottom: 22 }}>
      <h2 style={{ margin: "8px 0 6px", fontSize: 20 }}>Labels</h2>
      <p style={{ color: C.sub, fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
        Tag conversations so you can see at a glance what still needs doing.
      </p>

      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 12 }}>
        {labels.map(l => (
          <span key={l.id} style={{ display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 999,
            background: l.colour + "26", color: l.colour, border: "1px solid " + l.colour + "55" }}>
            {l.name}
            <button onClick={() => del(l.id)} style={{ background: "none", border: 0,
              color: l.colour, cursor: "pointer", padding: 0, display: "flex" }}><X size={12}/></button>
          </span>
        ))}
        {labels.length === 0 && <span style={{ color: C.sub, fontSize: 13 }}>No labels yet.</span>}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === "Enter" && add()}
          placeholder="New label, e.g. Awaiting payment"
          style={{ ...inp, marginBottom: 0, flex: 1 }}/>
        <div style={{ display: "flex", gap: 4 }}>
          {LABEL_COLOURS.map(c => (
            <button key={c} onClick={() => setColour(c)} title="Colour"
              style={{ width: 22, height: 22, borderRadius: 11, background: c, cursor: "pointer",
                border: colour === c ? "2px solid #fff" : "1px solid " + C.border }}/>
          ))}
        </div>
        <button onClick={add} style={{ ...iconBtn, padding: 8 }}><Plus size={17}/></button>
      </div>
    </div>
  );
}

function Chats() {
  const [convos, setConvos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [labels, setLabels] = useState<any[]>([]);
  const [assigned, setAssigned] = useState<any>({});
  const [picking, setPicking] = useState<string | null>(null);
  const [draft, setDraft] = useState<any>({});
  const [sending, setSending] = useState<string | null>(null);
  const [quick, setQuick] = useState<any[]>([]);

  useEffect(() => {
    api("/api/v1/business/quick-replies").then(d => setQuick(d.replies || [])).catch(() => {});
  }, []);

  // Typing /shortcut then a space expands it, the way WhatsApp Business does.
  const onDraft = (contactId: string, val: string) => {
    const m = val.match(/^\/([a-z0-9-]+)\s$/i);
    if (m) {
      const q = quick.find(x => x.shortcut === m[1].toLowerCase());
      if (q) { setDraft({ ...draft, [contactId]: q.body }); return; }
    }
    setDraft({ ...draft, [contactId]: val });
  };

  const send = async (contactId: string) => {
    const text = (draft[contactId] || "").trim();
    if (!text) return;
    setSending(contactId);
    try {
      await api("/api/v1/waba/send", "POST", { to: contactId, message: text });
      // Record what was sent, or the conversation loses half of itself.
      const c2 = convos.find((x: any) => x.contact_id === contactId);
      await api("/api/v1/messages/store", "POST", {
        contact_id: contactId,
        contact_name: c2?.contact_name || contactId,
        message_text: text,
        direction: "outgoing",
        was_sent: true,
      }).catch(() => {});
      setDraft({ ...draft, [contactId]: "" });
      load();
    } catch (e: any) { setErr(e.message); }
    setSending(null);
  };

  const loadLabels = () => api("/api/v1/business/labels")
    .then(d => { setLabels(d.labels || []); setAssigned(d.assigned || {}); })
    .catch(() => {});
  useEffect(() => { loadLabels(); }, []);

  const toggleLabel = async (contactId: string, labelId: number) => {
    const has = (assigned[contactId] || []).includes(labelId);
    setAssigned({ ...assigned, [contactId]: has
      ? (assigned[contactId] || []).filter((x: number) => x !== labelId)
      : [...(assigned[contactId] || []), labelId] });
    try {
      await api("/api/v1/business/labels/assign", "POST",
        { contact_id: contactId, label_id: labelId, remove: has });
    } catch (e) { loadLabels(); }
  };

  const load = () => {
    setErr("");
    api("/api/v1/messages/history")
      .then(d => { setConvos(d.messages || []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div style={{ color: C.sub, padding: 20 }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ margin: "8px 0 6px", fontSize: 20 }}>Customer messages</h2>
        <button onClick={load} style={iconBtn} title="Refresh"><RefreshCw size={15}/></button>
      </div>
      {err && <div style={{ color: C.bad, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {convos.length === 0 ? (
        <div style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 14,
          padding: 30, textAlign: "center", color: C.sub, fontSize: 14, lineHeight: 1.6 }}>
          No messages yet. Once your WhatsApp is connected, customer conversations appear here.
        </div>
      ) : convos.map((c: any, i: number) => (
        <div key={c.contact_id || i} style={{ background: C.card, border: "1px solid " + C.border,
          borderRadius: 14, padding: 14, marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 11, alignItems: "center" }}>
            <div style={{ width: 38, height: 38, borderRadius: 19, background: C.accent,
              display: "grid", placeItems: "center", color: "#fff", fontWeight: 700, flexShrink: 0 }}>
              {(c.contact_name || c.contact_id || "?")[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{c.contact_name || c.contact_id}</div>
              <div style={{ fontSize: 12.5, color: C.sub, overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c.message_text || c.original_message || ""}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center", marginTop: 9 }}>
            {(assigned[c.contact_id] || []).map((id: number) => {
              const l = labels.find(x => x.id === id);
              return l ? (
                <span key={id} style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px",
                  borderRadius: 999, background: l.colour + "26", color: l.colour,
                  border: "1px solid " + l.colour + "55" }}>{l.name}</span>
              ) : null;
            })}
            {labels.length > 0 && (
              <button onClick={() => setPicking(picking === c.contact_id ? null : c.contact_id)}
                style={{ ...iconBtn, fontSize: 11, gap: 4, padding: "3px 7px" }}>
                <Tags size={12}/> Label
              </button>
            )}
          </div>
          {picking === c.contact_id && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 7,
              padding: 9, background: "#0F1626", borderRadius: 10 }}>
              {labels.map(l => {
                const on = (assigned[c.contact_id] || []).includes(l.id);
                return (
                  <button key={l.id} onClick={() => toggleLabel(c.contact_id, l.id)}
                    style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 999,
                      cursor: "pointer", background: on ? l.colour : "transparent",
                      color: on ? "#fff" : l.colour, border: "1px solid " + l.colour }}>
                    {l.name}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", gap: 7, marginTop: 10 }}>
            <input value={draft[c.contact_id] || ""} placeholder="Reply, or type /shortcut"
              onChange={e => onDraft(c.contact_id, e.target.value)}
              onKeyDown={e => e.key === "Enter" && send(c.contact_id)}
              style={{ ...inp, marginBottom: 0, flex: 1, fontSize: 13 }}/>
            <button onClick={() => send(c.contact_id)}
              disabled={sending === c.contact_id || !(draft[c.contact_id] || "").trim()}
              style={{ ...iconBtn, background: C.accent, color: "#fff", borderRadius: 9,
                padding: "0 13px", opacity: sending === c.contact_id ? .6 : 1 }}>
              <Send size={15}/>
            </button>
          </div>
          {(c.ai_reply || c.reply_suggestion) && (
            <div style={{ marginTop: 10, background: "#0F1626", borderRadius: 10, padding: 11 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: C.accent, marginBottom: 4,
                letterSpacing: .4 }}>AI REPLY</div>
              <div style={{ fontSize: 13, lineHeight: 1.55 }}>{c.ai_reply || c.reply_suggestion}</div>
              <button onClick={() => setDraft({ ...draft, [c.contact_id]: c.ai_reply || c.reply_suggestion })}
                style={{ ...iconBtn, fontSize: 11, marginTop: 7, padding: 0, color: C.accent }}>
                Use this reply
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Profile({ onOut }: { onOut: () => void }) {
  const [tab, setTab] = useState<"chats" | "setup">("chats");
  const [v, setV] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api("/api/v1/business/profile")
      .then(d => { setV(d.profile || {}); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, []);

  const save = async () => {
    setErr(""); setSaved(false);
    try { await api("/api/v1/business/profile", "POST", v); setSaved(true); setTimeout(() => setSaved(false), 2500); }
    catch (e: any) { setErr(e.message); }
  };

  if (loading) return <div style={{ minHeight: "100vh", background: C.bg, color: C.sub, display: "grid", placeItems: "center" }}>Loading...</div>;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text }}>
      <div style={{ borderBottom: "1px solid " + C.border, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 18, fontWeight: 800, display: "flex", alignItems: "center", gap: 9 }}>
          <img src="/logo.png" alt="" width={26} height={26} style={{ borderRadius: 6 }}/>
          <span>JediChat<span style={{ color: C.accent }}>AI</span>
          <span style={{ color: C.sub, fontWeight: 500, fontSize: 13 }}>  Business</span></span></div>
        <button onClick={onOut} style={{ background: "none", border: 0, color: C.sub, cursor: "pointer", display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
          <LogOut size={15}/> Sign out
        </button>
      </div>

      <div style={{ display: "flex", gap: 4, padding: "10px 20px 0", maxWidth: 640,
        margin: "0 auto", borderBottom: "1px solid " + C.border }}>
        {[["chats", "Chats", MessageCircle], ["setup", "Setup", Settings2]].map(([k, label, Icon]: any) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ background: "none", border: 0, borderBottom: "2px solid " +
              (tab === k ? C.accent : "transparent"), color: tab === k ? C.text : C.sub,
              padding: "9px 14px", cursor: "pointer", fontSize: 14, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 7 }}>
            <Icon size={15}/> {label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: 20 }}>
        {tab === "chats" ? <Chats/> : <>
        <Verification/>
        <WabaConnect/>
        <QrConnect/>
        <Team/>
        <Gaps/>
        <Catalog/>
        <LabelManager/>
        <QuickReplies/>
        <h2 style={{ margin: "8px 0 6px", fontSize: 20 }}>Your business profile</h2>
        <p style={{ color: C.sub, fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
          The AI answers customers using only what you put here. Leave a field
          empty and it will say it needs to check rather than guess.
        </p>

        {FIELDS.map(f => {
          const Icon = f.icon;
          return (
            <div key={f.key} style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 14, padding: 14, marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <Icon size={15} color={C.accent}/>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{f.label}</span>
              </div>
              {f.select ? (
                <select value={v[f.key] || ""} onChange={e => setV({ ...v, [f.key]: e.target.value })}
                  style={{ ...inp, marginBottom: 0 }}>
                  <option value="">Choose your time zone</option>
                  {ZONES.map(z => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
                </select>
              ) : f.lines ? (
                <textarea value={v[f.key] || ""} placeholder={f.hint}
                  onChange={e => setV({ ...v, [f.key]: e.target.value })}
                  rows={f.lines} style={{ ...inp, marginBottom: 0, resize: "vertical", fontFamily: "inherit" }}/>
              ) : (
                <input value={v[f.key] || ""} placeholder={f.hint}
                  onChange={e => setV({ ...v, [f.key]: e.target.value })} style={{ ...inp, marginBottom: 0 }}/>
              )}
            </div>
          );
        })}

        {err && <div style={{ color: C.bad, fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <button onClick={save} style={{ ...btn, background: saved ? C.ok : C.accent }}>
          {saved ? <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Check size={16}/> Saved</span> : "Save profile"}
        </button>
        </>}
        <div style={{ height: 40 }}/>
      </div>
    </div>
  );
}

const inp: any = {
  width: "100%", padding: "11px 13px", marginBottom: 12, borderRadius: 10,
  border: "1px solid #232B45", background: "#0F1626", color: "#F2F4F8",
  fontSize: 14, outline: "none", boxSizing: "border-box",
};
const iconBtn: any = {
  background: "none", border: 0, color: "#8E93A8", cursor: "pointer",
  padding: 5, display: "flex", alignItems: "center",
};
const btn: any = {
  width: "100%", padding: "13px", borderRadius: 10, border: 0,
  background: "#007AFF", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer",
};

export default function App() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("jedichatai_token"));

  // An invite link lands here before sign-in, so hold the code until there is
  // an account to attach it to.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("join");
    if (code) {
      localStorage.setItem("jedichatai_pending_invite", code);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const code = localStorage.getItem("jedichatai_pending_invite");
    if (!authed || !code) return;
    api("/api/v1/business/team/accept", "POST", { invite_code: code })
      .catch(() => {})
      .finally(() => localStorage.removeItem("jedichatai_pending_invite"));
  }, [authed]);
  if (!authed) return <Login onIn={() => setAuthed(true)}/>;
  return <Profile onOut={() => { localStorage.removeItem("jedichatai_token"); setAuthed(false); }}/>;
}
